import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { ImportantNoticeSource } from '../../../common/model/important-notice';
import { ImportantNoticeService } from '../../../common/service/data/important-notice.service';
import { ImportantNoticePresentationService } from '../../../common/service/important-notice-presentation.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { AstralButtonComponent } from '../../../core/component/astral-components/astral-button/astral-button.component';
import { AstralDialogComponent } from '../../../core/component/astral-components/astral-dialog/astral-dialog.component';
import { MessageService } from '../../../core/service/message.service';

const OCTOBER_NEWS_SOURCE = ImportantNoticeSource.Internal;
const OCTOBER_NEWS_ID = 'hogia-fastighet-anvandartraff-2026-last-chance';
const OCTOBER_NEWS_STARTS_AT = Date.parse('2026-09-23T00:00:00+02:00');
const OCTOBER_NEWS_ENDS_AT = Date.parse('2026-10-13T00:00:00+02:00');

@Component({
  selector: 'app-overview-notice',
  standalone: true,
  imports: [AstralButtonComponent, AstralDialogComponent, TranslatePipe],
  templateUrl: './overview-notice.component.html',
  styleUrls: ['./overview-notice.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverviewNoticeComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly userAccountService: UserAccountService,
    private readonly importantNoticeService: ImportantNoticeService,
    private readonly presentationService: ImportantNoticePresentationService,
    private readonly messageService: MessageService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  showOctoberNews = false;
  showNewsDismissalConfirmation = false;
  octoberNewsDismissalSaving = false;
  octoberNewsDismissedForCurrentUser = false;
  octoberNewsShownThisSession = false;

  ngOnInit(): void {
    this.userAccountService.current.pipe(takeUntil(this.destroy$)).subscribe({
      next: account => {
        if (account.hasAccess(ModuleNames.System, Features.Overview) && this.isOctoberNewsActive()) {
          this.loadOctoberNewsDismissalStatus();
        }
      },
      error: error => this.messageService.showError(error),
    });
  }

  openOctoberNews(): void {
    if (this.octoberNewsDismissedForCurrentUser) return;
    this.markOctoberNewsAsShownThisSession();
    this.showOctoberNews = true;
  }

  closeOctoberNews(): void {
    this.showOctoberNews = false;
  }

  requestOctoberNewsDismissal(): void {
    this.showNewsDismissalConfirmation = true;
  }

  cancelOctoberNewsDismissal(): void {
    if (!this.octoberNewsDismissalSaving) this.showNewsDismissalConfirmation = false;
  }

  confirmOctoberNewsDismissal(): void {
    if (this.octoberNewsDismissalSaving) return;

    this.octoberNewsDismissalSaving = true;
    this.importantNoticeService
      .dismiss(OCTOBER_NEWS_SOURCE, OCTOBER_NEWS_ID)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.octoberNewsDismissedForCurrentUser = true;
          this.octoberNewsDismissalSaving = false;
          this.showNewsDismissalConfirmation = false;
          this.showOctoberNews = false;
          this.cdr.markForCheck();
        },
        error: error => {
          this.octoberNewsDismissalSaving = false;
          this.messageService.showError(error);
          this.cdr.markForCheck();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadOctoberNewsDismissalStatus(): void {
    this.importantNoticeService
      .getDismissal(OCTOBER_NEWS_SOURCE, OCTOBER_NEWS_ID)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: status => {
          this.octoberNewsDismissedForCurrentUser = status.isDismissed;
          this.octoberNewsShownThisSession = this.presentationService.hasBeenShown(OCTOBER_NEWS_SOURCE, OCTOBER_NEWS_ID);
          if (
            !status.isDismissed &&
            !this.octoberNewsShownThisSession &&
            !this.showOctoberNews &&
            !this.showNewsDismissalConfirmation
          ) {
            this.openOctoberNews();
          }
          this.cdr.markForCheck();
        },
        error: error => this.messageService.showError(error),
      });
  }

  private markOctoberNewsAsShownThisSession(): void {
    this.presentationService.markAsShown(OCTOBER_NEWS_SOURCE, OCTOBER_NEWS_ID);
    this.octoberNewsShownThisSession = true;
  }

  private isOctoberNewsActive(): boolean {
    const now = Date.now();
    return now >= OCTOBER_NEWS_STARTS_AT && now < OCTOBER_NEWS_ENDS_AT;
  }
}
